import { useState, useEffect, useMemo } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { TaskAttemptDetail } from "../api/examApi";
import { createTaskAttempt } from "../api/exam";
import { getToken, getUserDataId } from "../utils/storage";
import { setExamMode } from "@/utils/examMode";
import { supabase } from "../api/supabaseClient";
import "../styles/SingleTaskFeedback.css";
import NextFocus from "@/components/NextFocus";
import upArrow from '../../public/images/Icon wrap.svg';
import micIcon from '../../public/images/singleFeedback.svg';
import rightarrow from '../../public/images/SVG.svg';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { isTask2MaintenanceEnabled } from "@/config/maintenance";
import Task2MaintenanceNotice from "@/components/task2/Task2MaintenanceNotice";


const SingleTaskFeedback = () => {
  const [isRetrying, setIsRetrying] = useState(false);
  const [isRetryingSameTopic, setIsRetryingSameTopic] = useState(false);
  const [isRetryingNewTopic, setIsRetryingNewTopic] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  // Step 1: Get from location.state
  const locationUserId = location.state?.userId;
  const locationExamId = location.state?.examId;
  const locationTaskAttemptId = location.state?.taskAttemptId;
  const locationTaskType = location.state?.taskType;

  const [isLoading, setIsLoading] = useState(true);
  const [attemptData, setAttemptData] = useState<any>(null);
  const [taskType, setTaskType] = useState<number | null>(
    locationTaskType ||
    (localStorage.getItem('current_task_type') ? parseInt(localStorage.getItem('current_task_type')!, 10) : null)
  );
  const [analysisData, setAnalysisData] = useState<any>(null);

  // Step 2: Fallback to localStorage
  const [userId, setUserId] = useState<string | null>(
    locationUserId || localStorage.getItem("current_user_id")
  );
  const [examId, setExamId] = useState<string | null>(
    locationExamId || localStorage.getItem("current_exam_id")
  );
  const [taskAttemptId, setTaskAttemptId] = useState<string | null>(
    locationTaskAttemptId || localStorage.getItem("current_task_attempt_id")
  );

  // Memoized data to prevent null reference errors
  const safeAttemptData = useMemo(() => {
    return attemptData && attemptData.length > 0 ? attemptData[0] : null;
  }, [attemptData]);

  const hasNoData = useMemo(() => {
    return (
      !attemptData ||
      attemptData.length === 0 ||
      attemptData[0]?.individualFeedback?.feedback === "Aucun transcript n’est disponible pour afficher les données."
    );
  }, [attemptData]);


  useEffect(() => {
    if (locationUserId) localStorage.setItem("current_user_id", locationUserId);
    if (locationExamId) localStorage.setItem("current_exam_id", locationExamId);
    if (locationTaskAttemptId) localStorage.setItem("current_task_attempt_id", locationTaskAttemptId);

    if (locationUserId) setUserId(locationUserId);
    if (locationExamId) setExamId(locationExamId);
    if (locationTaskAttemptId) setTaskAttemptId(locationTaskAttemptId);

    // Set task type from location state if available (for dynamic retry button)
    if (locationTaskType) {
      setTaskType(locationTaskType);
      localStorage.setItem('current_task_type', locationTaskType.toString());
      console.log('✅ Task type set from location state:', locationTaskType);
    } else {
      // Check for stored retry task type (when returning from retry)
      const storedRetryTaskType = localStorage.getItem('current_retry_task_type');
      if (storedRetryTaskType) {
        const taskTypeNum = parseInt(storedRetryTaskType, 10);
        setTaskType(taskTypeNum);
        localStorage.setItem('current_task_type', taskTypeNum.toString());
        console.log('✅ Task type set from stored retry context:', taskTypeNum);
        // Clear the stored retry task type after using it
        localStorage.removeItem('current_retry_task_type');
      }
    }

    // Load analysis data from localStorage if available (for immediate display)
    const storedAnalysisData = localStorage.getItem('latest_analysis_data');
    if (storedAnalysisData) {
      try {
        const parsedData = JSON.parse(storedAnalysisData);
        setAnalysisData(parsedData);
        console.log('✅ Loaded analysis data from localStorage:', parsedData);
        console.log('🔍 Micro-tips count:', parsedData.micro_tips?.length || 0);
        console.log('🔍 Micro-tips data:', parsedData.micro_tips);
        // Clear the stored data after loading
        localStorage.removeItem('latest_analysis_data');
      } catch (error) {
        console.error('❌ Error parsing stored analysis data:', error);
      }
    }

    // Fallback: If userId is still null, try to get it from user_data
    if (!userId) {
      const userDataId = getUserDataId();
      if (userDataId) {
        setUserId(userDataId);
        localStorage.setItem("current_user_id", userDataId);
        console.log('✅ User ID set from user_data:', userDataId);
      }
    }
  }, [locationUserId, locationExamId, locationTaskAttemptId, userId]);

  const handleRetryTask1 = async () => {
    try {
      setIsRetrying(true);
      console.log('🎯 RETRY TASK 1 - Starting retry process...');

      // Unlock audio context for iPhone compatibility (same as MicTest and TaskOne)
      try {
        const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (AC) {
          let ctx: AudioContext | undefined = (window as any).__appAudioContext;
          if (!ctx || ctx.state === 'closed') {
            console.log('🔧 Creating new AudioContext in SingleTaskFeedback...');
            ctx = new AC();
            (window as any).__appAudioContext = ctx;
          }

          console.log('🔧 AudioContext state before resume:', ctx.state);

          if (ctx.state !== 'running') {
            console.log('🔧 Resuming AudioContext...');
            await ctx.resume();
            console.log('🔧 AudioContext state after resume:', ctx.state);
          }

          // Additional mobile-specific unlock
          if (ctx.state === 'suspended') {
            console.log('🔧 AudioContext still suspended, trying again...');
            await new Promise(resolve => setTimeout(resolve, 100));
            await ctx.resume();
          }

          // Keep audio context alive on mobile
          const keepAlive = () => {
            if (ctx && ctx.state === 'suspended') {
              ctx.resume().catch(() => { });
            }
          };

          // Resume audio context on user interaction (mobile requirement)
          document.addEventListener('touchstart', keepAlive, { once: true });
          document.addEventListener('touchend', keepAlive, { once: true });
          document.addEventListener('click', keepAlive, { once: true });
        }
        console.log('🔓 Audio context unlocked for Task 1 retry (mobile optimized)');
      } catch (e) {
        console.warn('⚠️ AudioContext unlock failed:', e);
      }

      // Add a small delay to ensure audio context is properly set up
      await new Promise(resolve => setTimeout(resolve, 200));

      const token = getToken();

      if (!examId) {
        console.error('❌ No session ID found for retry');
        alert('No session found. Please start a new exam.');
        return;
      }

      if (!token) {
        console.error('❌ No auth token found for retry');
        alert('Authentication required. Please log in again.');
        return;
      }

      // Delete the previous transcript
      try {
        console.log(`🗑️ Deleting transcript for examId: ${examId}, task: task1`);
        const { error: deleteError } = await supabase.functions.invoke('delete_transcript', {
          body: { examId, task: 'task1' },
        });
        if (deleteError) {
          console.error('❌ Error deleting transcript:', deleteError);
        } else {
          console.log('✅ Transcript deleted successfully');
        }
      } catch (error) {
        console.error('❌ Exception deleting transcript:', error);
      }

      console.log('🎯 RETRY TASK 1 - Creating task attempt for session:', examId);

      // Create a new task attempt entry
      const attemptResponse = await createTaskAttempt({
        sessionId: examId,
        taskType: 1 // Task 1
      });

      if (attemptResponse.success) {
        console.log('✅ RETRY TASK 1 - Task attempt created successfully:', attemptResponse);

        // Store the attempt ID for later use when task is completed
        if (attemptResponse.taskAttemptId) {
          localStorage.setItem('current_task_attempt_id', attemptResponse.taskAttemptId);
          console.log('💾 RETRY TASK 1 - Stored attempt ID:', attemptResponse.taskAttemptId);
        }

        // Store task type for retry context
        if (taskType) {
          localStorage.setItem('current_retry_task_type', taskType.toString());
          console.log('💾 RETRY TASK 1 - Stored task type for retry context:', taskType);
        }

        // Show success message with attempt number
        const attemptNumber = attemptResponse.attemptNumber || 1;
        console.log(`🎯 RETRY TASK 1 - Starting attempt #${attemptNumber}...`);

        // Individual Task retry is Full/paid authority.
        setExamMode("full");
        // Navigate to Task 1 with the existing session ID
        console.log('🎯 RETRY TASK 1 - Navigating to Task 1...');
        navigate('/task-one', { state: { examId: examId } });
      } else {
        console.error('❌ RETRY TASK 1 - Failed to create task attempt:', attemptResponse.message);
        alert('Failed to start retry. Please try again.');
      }
    } catch (error) {
      console.error('❌ RETRY TASK 1 - Error during retry:', error);
      // alert('An error occurred while starting the retry. Please try again.');
    } finally {
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    const fetchTaskAttempt = async () => {
      const token = getToken();
      const examId =
        localStorage.getItem("combined_feedback_exam_id") ||
        localStorage.getItem("current_exam_id") ||
        locationExamId;


      // Get userId with fallback to user_data if not available
      let currentUserId = userId;
      if (!currentUserId) {
        currentUserId = getUserDataId();
        if (currentUserId) {
          setUserId(currentUserId);
          localStorage.setItem("current_user_id", currentUserId);
          console.log('✅ User ID fallback in fetchTaskAttempt:', currentUserId);
        }
      }

      if (!examId || !token || !currentUserId || !taskAttemptId) {
        console.log('❌ Missing required data:', { examId, token: !!token, userId: currentUserId, taskAttemptId });
        alert("Missing session, token, user ID, or attempt ID. Please try again.");
        setIsLoading(false);
        return;
      }

      try {
        const response = await TaskAttemptDetail({
          sessionId: examId,
          token,
          userId: currentUserId,
          id: taskAttemptId,
        });

        if (!response) {
          console.log("❌ No response from API");
          setIsLoading(false);
          return;
        }

        let formattedData = [];
        console.log("🔍 Raw API response:", response);
        // Single task response
        if (response.task) {
          formattedData.push({
            attemptId: response.id,
            createdAt: response.created_at,
            taskType: response.task_type,
            transcript: response.transcript,
            rewrittenResponse: response.rewritten_response,
            estimatedCefr: response.estimated_cefr,
            grammarTips: response.grammar_tips,
            vocabTips: response.vocab_tips,
            grammar: response.grammar,
            vocabulary: response.vocabulary,
            comprehension: response.comprehension,
            fluency: response.fluency,
            pronunciation: response.pronunciation,
            averageScore: response.average_score,
            strength: response.strength,
            weakness: response.weakness,
            transcriptSnippets: response.transcript_snippets || [],
            individual_feedback_json: [response.individual_feedback_json],
            // nativeExamples: response.native_examples?.filter(ex => ex.native_example).map(ex => ex.native_example) || [],
            nativeExamples: response.native_examples?.map(ex => ({
              nativeExample: ex.native_example,
              userText: ex.user_text,
              textFr: ex.text_fr
            })) || [],
            microTips: response.micro_tips?.map(tip => ({
              type: tip.type,
              label: tip.label_en,
              details: tip.details_en,
            })) || [],
          });

        }
        // Multiple attempts response
        else if (response.attempts) {
          formattedData = response.attempts.map(attempt => ({
            attemptId: attempt.id,
            createdAt: attempt.created_at,
            taskType: attempt.task_type,
            transcript: attempt.transcript,
            rewrittenResponse: attempt.rewritten_response,
            estimatedCefr: attempt.estimated_cefr,
            grammarTips: attempt.grammar_tips,
            vocabTips: attempt.vocab_tips,
            grammar: attempt.grammar,
            vocabulary: attempt.vocabulary,
            comprehension: attempt.comprehension,
            fluency: attempt.fluency,
            pronunciation: attempt.pronunciation,
            averageScore: attempt.average_score,
            strength: attempt.strength,
            weakness: attempt.weakness,
            transcriptSnippets: attempt.transcript_snippets || [],
            individualFeedback: attempt.individual_feedback_json,
            // nativeExamples: attempt.native_examples?.map(ex => ex.native_example) || [],
            nativeExamples: attempt.native_examples?.map(ex => ({
              nativeExample: ex.native_example,
              userText: ex.user_text,
              textFr: ex.text_fr
            })) || [],
            microTips: attempt.micro_tips?.map(tip => ({
              type: tip.type,
              label: tip.label_en,
              details: tip.details_en,
            })) || [],
          }));
        }
        console.log("✅ Fetched task attempt data:", formattedData);
        setAttemptData(formattedData);

        // Set task type from the first attempt or fallback to location state
        if (formattedData.length > 0 && formattedData[0].taskType) {
          setTaskType(formattedData[0].taskType);
          localStorage.setItem('current_task_type', formattedData[0].taskType.toString());
          console.log("✅ Task type set from API data:", formattedData[0].taskType);
        } else if (locationTaskType) {
          setTaskType(locationTaskType);
          localStorage.setItem('current_task_type', locationTaskType.toString());
          console.log("✅ Using task type from location state as fallback:", locationTaskType);
        }

        console.log("✅ Formatted attempt data:", formattedData);
        console.log("🔍 Raw response:", response);

      } catch (error) {
        console.error("❌ Error fetching task attempt:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchTaskAttempt();
  }, [userId, examId, taskAttemptId]);



  const handleRetryTask2 = async (useSameTopic: boolean = true) => {
    try {
      if (useSameTopic) {
        setIsRetryingSameTopic(true);
      } else {
        setIsRetryingNewTopic(true);
      }
      console.log('🎯 RETRY TASK 2 - Starting retry process...', { useSameTopic });

      // Unlock audio context for iPhone compatibility (same as MicTest and TaskOne)
      try {
        const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (AC) {
          let ctx: AudioContext | undefined = (window as any).__appAudioContext;
          if (!ctx || ctx.state === 'closed') {
            console.log('🔧 Creating new AudioContext in SingleTaskFeedback...');
            ctx = new AC();
            (window as any).__appAudioContext = ctx;
          }

          console.log('🔧 AudioContext state before resume:', ctx.state);

          if (ctx.state !== 'running') {
            console.log('🔧 Resuming AudioContext...');
            await ctx.resume();
            console.log('🔧 AudioContext state after resume:', ctx.state);
          }

          // Additional mobile-specific unlock
          if (ctx.state === 'suspended') {
            console.log('🔧 AudioContext still suspended, trying again...');
            await new Promise(resolve => setTimeout(resolve, 100));
            await ctx.resume();
          }

          // Keep audio context alive on mobile
          const keepAlive = () => {
            if (ctx && ctx.state === 'suspended') {
              ctx.resume().catch(() => { });
            }
          };

          // Resume audio context on user interaction (mobile requirement)
          document.addEventListener('touchstart', keepAlive, { once: true });
          document.addEventListener('touchend', keepAlive, { once: true });
          document.addEventListener('click', keepAlive, { once: true });
        }
        console.log('🔓 Audio context unlocked for Task 2 retry (mobile optimized)');
      } catch (e) {
        console.warn('⚠️ AudioContext unlock failed:', e);
      }

      // Add a small delay to ensure audio context is properly set up
      await new Promise(resolve => setTimeout(resolve, 200));

      // const sessionId = getSessionId();
      const token = getToken();

      if (!examId) {
        console.error('❌ No session ID found for retry');
        alert('No session found. Please start a new exam.');
        return;
      }

      if (!token) {
        console.error('❌ No auth token found for retry');
        alert('Authentication required. Please log in again.');
        return;
      }

      // Delete the previous transcript
      try {
        console.log(`🗑️ Deleting transcript for examId: ${examId}, task: task2`);
        const { error: deleteError } = await supabase.functions.invoke('delete_transcript', {
          body: { examId, task: 'task2' },
        });
        if (deleteError) {
          console.error('❌ Error deleting transcript:', deleteError);
        } else {
          console.log('✅ Transcript deleted successfully');
        }
      } catch (error) {
        console.error('❌ Exception deleting transcript:', error);
      }

      console.log('🎯 RETRY TASK 2 - Creating task attempt for session:', examId);

      // Create a new task attempt entry
      const attemptResponse = await createTaskAttempt({
        sessionId: examId,
        taskType: 2 // Task 2
      });

      if (attemptResponse.success) {
        console.log('✅ RETRY TASK 2 - Task attempt created successfully:', attemptResponse);

        // Store the attempt ID for later use when task is completed
        if (attemptResponse.taskAttemptId) {
          localStorage.setItem('current_task_attempt_id', attemptResponse.taskAttemptId);
          console.log('💾 RETRY TASK 2 - Stored attempt ID:', attemptResponse.taskAttemptId);
        }

        // Store topic preference for Task 2
        localStorage.setItem('task2_retry_use_same_topic', useSameTopic.toString());
        console.log('💾 RETRY TASK 2 - Stored topic preference:', useSameTopic);

        // Store task type for retry context
        if (taskType) {
          localStorage.setItem('current_retry_task_type', taskType.toString());
          console.log('💾 RETRY TASK 2 - Stored task type for retry context:', taskType);
        }

        // Show success message with attempt number
        const attemptNumber = attemptResponse.attemptNumber || 1;
        console.log(`🎯 RETRY TASK 2 - Starting attempt #${attemptNumber}...`);

        // Individual Task retry is Full/paid authority.
        setExamMode("full");
        // Navigate to Task 2 with the existing session ID
        console.log('🎯 RETRY TASK 2 - Navigating to Task 2...');
        navigate('/task-two', { state: { examId: examId } });
      } else {
        console.error('❌ RETRY TASK 2 - Failed to create task attempt:', attemptResponse.message);
        alert('Failed to start retry. Please try again.');
      }
    } catch (error) {
      console.error('❌ RETRY TASK 2 - Error during retry:', error);
      alert('An error occurred while starting the retry. Please try again.');
    } finally {
      if (useSameTopic) {
        setIsRetryingSameTopic(false);
      } else {
        setIsRetryingNewTopic(false);
      }
    }
  };

  const handleRetryTask3 = async () => {
    try {
      setIsRetrying(true);
      console.log('🎯 RETRY TASK 3 - Starting retry process...');

      // Unlock audio context for iPhone compatibility (same as MicTest and TaskOne)
      try {
        const AC: any = (window as any).AudioContext || (window as any).webkitAudioContext;
        if (AC) {
          let ctx: AudioContext | undefined = (window as any).__appAudioContext;
          if (!ctx || ctx.state === 'closed') {
            console.log('🔧 Creating new AudioContext in SingleTaskFeedback...');
            ctx = new AC();
            (window as any).__appAudioContext = ctx;
          }

          console.log('🔧 AudioContext state before resume:', ctx.state);

          if (ctx.state !== 'running') {
            console.log('🔧 Resuming AudioContext...');
            await ctx.resume();
            console.log('🔧 AudioContext state after resume:', ctx.state);
          }

          // Additional mobile-specific unlock
          if (ctx.state === 'suspended') {
            console.log('🔧 AudioContext still suspended, trying again...');
            await new Promise(resolve => setTimeout(resolve, 100));
            await ctx.resume();
          }

          // Keep audio context alive on mobile
          const keepAlive = () => {
            if (ctx && ctx.state === 'suspended') {
              ctx.resume().catch(() => { });
            }
          };

          // Resume audio context on user interaction (mobile requirement)
          document.addEventListener('touchstart', keepAlive, { once: true });
          document.addEventListener('touchend', keepAlive, { once: true });
          document.addEventListener('click', keepAlive, { once: true });
        }
        console.log('🔓 Audio context unlocked for Task 3 retry (mobile optimized)');
      } catch (e) {
        console.warn('⚠️ AudioContext unlock failed:', e);
      }

      // Add a small delay to ensure audio context is properly set up
      await new Promise(resolve => setTimeout(resolve, 200));

      const token = getToken();

      if (!examId) {
        console.error('❌ No session ID found for retry');
        alert('No session found. Please start a new exam.');
        return;
      }

      if (!token) {
        console.error('❌ No auth token found for retry');
        alert('Authentication required. Please log in again.');
        return;
      }

      // Delete the previous transcript
      try {
        console.log(`🗑️ Deleting transcript for examId: ${examId}, task: task3`);
        const { error: deleteError } = await supabase.functions.invoke('delete_transcript', {
          body: { examId, task: 'task3' },
        });
        if (deleteError) {
          console.error('❌ Error deleting transcript:', deleteError);
        } else {
          console.log('✅ Transcript deleted successfully');
        }
      } catch (error) {
        console.error('❌ Exception deleting transcript:', error);
      }

      console.log('🎯 RETRY TASK 3 - Creating task attempt for session:', examId);

      // Create a new task attempt entry
      const attemptResponse = await createTaskAttempt({
        sessionId: examId,
        taskType: 3 // Task 3
      });

      if (attemptResponse.success) {
        console.log('✅ RETRY TASK 3 - Task attempt created successfully:', attemptResponse);

        // Store the attempt ID for later use when task is completed
        if (attemptResponse.taskAttemptId) {
          localStorage.setItem('current_task_attempt_id', attemptResponse.taskAttemptId);
          console.log('💾 RETRY TASK 3 - Stored attempt ID:', attemptResponse.taskAttemptId);
        }

        // Store task type for retry context
        if (taskType) {
          localStorage.setItem('current_retry_task_type', taskType.toString());
          console.log('💾 RETRY TASK 3 - Stored task type for retry context:', taskType);
        }

        // Show success message with attempt number
        const attemptNumber = attemptResponse.attemptNumber || 1;
        console.log(`🎯 RETRY TASK 3 - Starting attempt #${attemptNumber}...`);

        // Individual Task retry is Full/paid authority.
        setExamMode("full");
        // Navigate to Task 3 with the existing session ID
        console.log('🎯 RETRY TASK 3 - Navigating to Task 3...');
        navigate('/task-three');
      } else {
        console.error('❌ RETRY TASK 3 - Failed to create task attempt:', attemptResponse.message);
        alert('Failed to start retry. Please try again.');
      }
    } catch (error) {
      console.error('❌ RETRY TASK 3 - Error during retry:', error);
      alert('An error occurred while starting the retry. Please try again.');
    } finally {
      setIsRetrying(false);
    }
  };

  // Show loading spinner while fetching data
  if (isLoading) {
    return (
      <div className="fixed inset-0 flex items-center justify-center bg-white z-50">
        <div className="flex flex-col items-center justify-center text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
          <p className="text-muted-foreground mt-4" style={{ fontSize: "18px" }}>Loading your results...</p>

        </div>
      </div>
    );
  }


  const getTaskMessage = (title) => {
    if (title === "Task 1") {
      return " — Self Introduction";
    }

    if (title === "Task 2") {
      return " —  Role Play Questions";
    }

    if (title === "Task 3") {
      return " — Opinion and Argument";
    }

    // default (if some other title)
    return "";
  };

  return (
    <div className="single-task-container">
      <div className="single-feedback-title">
        <div className="title-img"><img src={micIcon} alt="" /></div>
        <div className="title-text-score">
          <span className="single-title">
            {attemptData && attemptData.length > 0 &&
              attemptData[0]?.individualFeedback?.title ? (
              <>
                {attemptData[0].individualFeedback.title}
                {getTaskMessage(attemptData[0].individualFeedback.title)}
              </>
            ) : (
              // fallback title text using taskType
              <>
                {`Task ${taskType || ""}`}
                {getTaskMessage(`Task ${taskType}`)}
              </>
            )}
          </span>

          {!hasNoData && attemptData && attemptData.length > 0 && attemptData[0]?.individualFeedback?.cefr_level && (
            <div className="title-text-cefr">
              <span>
                {attemptData[0].individualFeedback.cefr_level}
              </span>
            </div>
          )}
        </div>

      </div>

      <div className="task-analysis-section">
        <div className='task-analysis-header-2'>
          <h2 className="task-analysis-title">
            Detailed Task Analysis
          </h2>
          <p className="task-analysis-subtitle">
            Review your responses and see how to improve
          </p>
        </div>

        <div>
          {(attemptData && attemptData.length > 0 ? attemptData : [null]).map((item: any, index: number) => (
            <div
              key={item?.attemptId ?? index}
              className="rounded-xl border p-6 shadow-sm bg-white space-y-4"
            >
              <div className="task-analysis-content">
                <div className="task-analysis-content-inner">

                  {/* Feedback */}
                  <div className="task-analysis-improved">
                    <div>
                      <h4>Feedback</h4>
                    </div>
                    <div>
                      <p>
                        {item?.individualFeedback?.feedback || "Aucun transcript n’est disponible pour afficher les données."}
                      </p>
                    </div>
                  </div>

                  {/* Reflection */}
                  <div className="task-analysis-improved">
                    <div>
                      <h4>Reflection</h4>
                    </div>
                    <div>
                      <p>
                        {item?.individualFeedback?.reflection || "Aucun transcript n’est disponible pour afficher les données."}
                      </p>
                    </div>
                  </div>

                  {/* Native level example */}
                  <Collapsible className="task-analysis-native-accordion">
                    <CollapsibleTrigger asChild>
                      <button className="task-analysis-accordion-trigger">
                        <h4>Native Level Example</h4>
                        <img src={upArrow} alt="" />
                      </button>
                    </CollapsibleTrigger>

                    <CollapsibleContent className="task-analysis-accordion-content">
                      <div className="task-analysis-accordion-inner">
                        <p className="whitespace-pre-wrap">
                          {item?.individualFeedback?.native_example?.text_fr || "Aucun transcript n’est disponible pour afficher les données."}
                        </p>
                      </div>
                    </CollapsibleContent>
                  </Collapsible>

                  {/* User Transcript */}
                  {/* <Collapsible className="task-analysis-native-accordion">
                    <CollapsibleTrigger asChild>
                      <button className="task-analysis-accordion-trigger">
                        <h4>Your Transcript</h4>
                        <img src={upArrow} alt="" />
                      </button>
                    </CollapsibleTrigger>

                    <CollapsibleContent className="task-analysis-accordion-content">
                      <div className="task-analysis-accordion-inner">
                        <p className="whitespace-pre-wrap">
                          {item?.transcript || "No Transcript Found"}
                        </p>
                      </div>
                    </CollapsibleContent>
                  </Collapsible> */}

                </div>
              </div>
            </div>
          ))}
        </div>

      </div>


      <div className="single-task-next-step">
        <div className="next-focus-container">
          <div>
            <div className="next-focus-left" style={{ textAlign: 'center' }}>
              Your Next Focus
            </div>
          </div>
          <div className="next-focus-container-inner">
            <NextFocus
              tips={
                attemptData?.[0]?.individualFeedback?.next_focus_tips?.length > 0
                  ? attemptData[0].individualFeedback.next_focus_tips
                  : []
              }
            />
          </div>
        </div>
      </div>


      <div className="single-task-retry-dashboard-section">

        <div className="backToDashboard" >
          <div className='task-analysis-footer-inner' onClick={() => navigate('/dashboard')} style={{ cursor: 'pointer' }}>
            <img src={rightarrow} alt="" />
            <button className="btn-text">
              Back to Dashboard
            </button>
          </div>
        </div>
        <div className="retry-single-task">
          <div className="single-task-button-retry-single">

            {taskType === 1 && (
              <button
                className="singlebtn"
                onClick={handleRetryTask1}
                disabled={isRetrying}
                style={{
                  opacity: isRetrying ? 0.7 : 1,
                  cursor: isRetrying ? 'not-allowed' : 'pointer'
                }}
              >
                {isRetrying ? 'Starting Retry...' : 'Practice Task 1 Again'}
              </button>
            )}


            {taskType === 2 && (
              <>
                {isTask2MaintenanceEnabled() ? (
                  <div className="w-full max-w-md space-y-4">
                    <Task2MaintenanceNotice compact />
                    <button
                      type="button"
                      className="singlebtn-2"
                      disabled
                      style={{ opacity: 0.65, cursor: "not-allowed" }}
                    >
                      Temporarily unavailable
                    </button>
                  </div>
                ) : (
                  <button
                    className="singlebtn-2"
                    onClick={() => handleRetryTask2(false)}
                    disabled={isRetryingNewTopic}
                    style={{
                      opacity: isRetryingNewTopic ? 0.7 : 1,
                      cursor: isRetryingNewTopic ? "not-allowed" : "pointer",
                    }}
                  >
                    {isRetryingNewTopic ? "Starting Retry..." : "Practice a New Topic"}
                  </button>
                )}
              </>
            )}


            {/* Task 3 Retry Button - Only show if viewing Task 3 feedback */}
            {taskType === 3 && (
              <button
                className="singlebtn"
                onClick={handleRetryTask3}
                disabled={isRetrying}
                style={{
                  opacity: isRetrying ? 0.7 : 1,
                  cursor: isRetrying ? 'not-allowed' : 'pointer'
                }}
              >
                {isRetrying ? 'Starting Retry...' : 'Practice a New Topic'}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>

  );
};

export default SingleTaskFeedback;
